import axios from 'axios';
import { User } from '@/contexts/AuthContext';

import { API_BASE_URL as BASE } from './apiConfig';
const API_BASE_URL = `${BASE}/api`;

// 백엔드 API 응답 타입 (실제 백엔드 스펙)
interface BackendLoginResponse {
  token: string;
  refreshToken: string;
  type: string;
  userId: number;
  email: string;
  name: string;
  role: string;
  businessNumber?: string;
  userRole?: string;
  subscriptionPlan?: string;
  preferredLanguage?: string;
  mustChangePassword?: boolean;
  loginType?: string;
  createdAt: string;
  lastLoginAt?: string;
}

const formatRetryAfterMessage = (retryAfterValue?: string): string | null => {
  if (!retryAfterValue) return null;

  const seconds = Number(retryAfterValue);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  if (seconds >= 60) {
    return `${Math.ceil(seconds / 60)}분 후 다시 시도해 주세요.`;
  }

  return `${seconds}초 후 다시 시도해 주세요.`;
};

// 어댑터: 백엔드 응답 → 프론트엔드 User 객체로 변환
function adaptBackendUserToFrontend(backendData: any): User {
  // userId 우선순위: userId 필드 > email 앞부분 > id > 'unknown'
  let userId = 'unknown';
  if (backendData.userId) {
    userId = backendData.userId;
  } else if (backendData.email) {
    userId = backendData.email.split('@')[0];
  } else if (backendData.id) {
    userId = backendData.id.toString();
  }

  return {
    userId,
    name: backendData.name || 'Unknown',
    email: backendData.email || '',
    phone: backendData.phone || undefined,
    companyName: backendData.companyName || backendData.company || undefined,
    businessNumber: backendData.businessNumber || undefined,
    userRole: (backendData.userRole === 'SUPER_ADMIN' || backendData.role === 'SUPER_ADMIN') ? 'SUPER_ADMIN' : 'CLIENT',
    approvalStatus: backendData.approvalStatus || 'APPROVED',
    subscriptionPlan: backendData.subscriptionPlan || 'FREE',
    preferredLanguage: backendData.preferredLanguage || 'ko',
    mustChangePassword: backendData.mustChangePassword || false,
    loginType: backendData.loginType || 'LOCAL',
    lastLoginAt: backendData.lastLoginAt,
    createdAt: backendData.createdAt,
  };
}

// 인증 API 어댑터
export const authAdapter = {
  // 로그인
  login: async (userId: string, password: string, rememberMe?: boolean) => {
    try {
      // 프론트엔드는 userId를 받지만, 백엔드는 email을 기대
      // userId를 email 형식으로 변환 (실제로는 userId를 email로 사용)
      const emailToSend = userId.includes('@') ? userId : `${userId}@temp.com`;
      
      const response = await axios.post<BackendLoginResponse>(`${API_BASE_URL}/auth/login`, {
        email: emailToSend,
        password,
      });

      // 백엔드 응답을 프론트엔드 형식으로 변환
      const user = adaptBackendUserToFrontend({
        id: response.data.userId,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
      });

      return {
        success: true,
        data: {
          token: response.data.token,
          user,
        },
      };
    } catch (error: any) {
      const retryAfterMessage = error.response?.status === 429
        ? formatRetryAfterMessage(error.response?.headers?.['retry-after'])
        : null;
      const message = retryAfterMessage
        ? `로그인 시도가 너무 많습니다. ${retryAfterMessage}`
        : (error.response?.data?.message || '로그인에 실패했습니다.');
      throw new Error(message);
    }
  },

  // 아이디 중복 확인
  checkUsername: async (userId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/check-username`, {
        params: { userId }
      });
      return {
        success: true,
        available: response.data.available,
      };
    } catch (error: any) {
      console.error('[authAdapter] checkUsername 에러:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      throw new Error(error.response?.data?.message || '아이디 확인 중 오류가 발생했습니다.');
    }
  },

  // 회원가입
  signup: async (signupData: any) => {
    try {
      // 프론트엔드 형식을 백엔드 형식으로 변환
      const backendData = {
        userId: signupData.userId,
        email: signupData.email,
        password: signupData.password,
        name: signupData.name,
        phone: signupData.phone,
        companyName: signupData.companyName,
        businessNumber: signupData.businessNumber,
        preferredLanguage: signupData.preferredLanguage,
        agreeTerms: signupData.agreeTerms,
        agreePrivacy: signupData.agreePrivacy,
        agreeMarketing: signupData.agreeMarketing,
      };

      const response = await axios.post<BackendLoginResponse>(`${API_BASE_URL}/auth/signup`, backendData);

      const user = adaptBackendUserToFrontend({
        id: response.data.userId,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        companyName: backendData.companyName,
      });

      return {
        success: true,
        message: '회원가입이 완료되었습니다.',
        data: {
          token: response.data.token,
          user,
        },
      };
    } catch (error: any) {
      const message = error.response?.data?.message || '회원가입에 실패했습니다.';
      throw new Error(message);
    }
  },

  // 현재 사용자 정보 조회
  getCurrentUser: async (token: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // 백엔드 응답 구조 확인: response.data 또는 response.data.data
      const backendData = response.data?.data || response.data;

      const user = adaptBackendUserToFrontend(backendData);

      return {
        success: true,
        data: user,
      };
    } catch (error: any) {
      console.error('[authAdapter] getCurrentUser error:', error.response?.data);
      throw new Error(error.response?.data?.message || '사용자 정보 조회에 실패했습니다.');
    }
  },

  // 로그아웃
  logout: async (token: string) => {
    try {
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { success: true };
    } catch (error) {
      // 로그아웃은 에러가 나도 로컬에서 처리
      return { success: true };
    }
  },

  // Google 로그인
  googleLogin: async (credential: string) => {
    try {
      const response = await axios.post<BackendLoginResponse>(`${API_BASE_URL}/auth/google`, {
        credential,
      });

      const user = adaptBackendUserToFrontend({
        id: response.data.userId,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        loginType: 'GOOGLE',
      });

      return {
        success: true,
        data: {
          token: response.data.token,
          user,
        },
      };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Google 로그인에 실패했습니다.';
      throw new Error(message);
    }
  },

  // 네이버 로그인
  naverLogin: async (code: string, state: string) => {
    try {
      const response = await axios.post<BackendLoginResponse>(`${API_BASE_URL}/auth/naver/callback`, {
        code,
        state,
      });

      const user = adaptBackendUserToFrontend({
        id: response.data.userId,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        loginType: 'NAVER',
      });

      return {
        success: true,
        data: {
          token: response.data.token,
          user,
        },
      };
    } catch (error: any) {
      const message = error.response?.data?.message || '네이버 로그인에 실패했습니다.';
      throw new Error(message);
    }
  },

  // 카카오 로그인
  kakaoLogin: async (code: string) => {
    try {
      const response = await axios.post<BackendLoginResponse>(`${API_BASE_URL}/auth/kakao/callback`, {
        code,
      });

      const user = adaptBackendUserToFrontend({
        id: response.data.userId,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        loginType: 'KAKAO',
      });

      return {
        success: true,
        data: {
          token: response.data.token,
          user,
        },
      };
    } catch (error: any) {
      const message = error.response?.data?.message || '카카오 로그인에 실패했습니다.';
      throw new Error(message);
    }
  },

  // 아이디 찾기 (이메일로 찾기)
  findUserId: async (name: string, email: string) => {
    try {
      // 백엔드에 아이디 찾기 API가 없으면 임시로 에러 반환
      // 실제로는 백엔드 구현 필요
      const response = await axios.post(`${API_BASE_URL}/auth/find-id`, {
        name,
        email,
      });

      // 백엔드에서 email을 반환하면 userId로 변환
      const userId = response.data.email?.split('@')[0] || response.data.userId;

      return {
        success: true,
        data: {
          userId,
        },
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '아이디를 찾을 수 없습니다.');
    }
  },

  // 비밀번호 재설정 요청
  requestPasswordReset: async (userId: string, email: string) => {
    try {
      // userId를 email로 변환하여 전송
      const emailToSend = userId.includes('@') ? userId : email;

      await axios.post(`${API_BASE_URL}/auth/reset-password-request`, {
        email: emailToSend,
      });

      return {
        success: true,
        message: '비밀번호 재설정 링크가 이메일로 발송되었습니다.',
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '비밀번호 재설정 요청에 실패했습니다.');
    }
  },

  // 비밀번호 재설정
  resetPassword: async (token: string, newPassword: string) => {
    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        token,
        newPassword,
      });

      return {
        success: true,
        message: '비밀번호가 성공적으로 변경되었습니다.',
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '비밀번호 재설정에 실패했습니다.');
    }
  },
};

// 주의: 이 파일은 과거 axios 패키지의 전역 싱글턴에 요청/응답 인터셉터를 등록했다.
// 요청 인터셉터는 Authorization 헤더를 전역 주입해서, 앱 어디서든(예: S3 presigned
// URL 직접 PUT 같은 제3자 도메인 호출) bare axios를 쓰는 순간 로그인 토큰이 함께
// 새어나가는 문제가 있어 제거했다 - 이 파일의 API들은 필요한 곳(auth/me, logout)에서
// Authorization을 개별적으로 명시한다.
// 응답 인터셉터(401 처리)도 AuthContext.tsx가 같은 axios 싱글턴에 등록하는 버전과
// 중복이었고, 그쪽과 달리 로그인 시도 자체의 401(비번 오류 등)을 예외 처리하지 않아
// 로그인 실패 시에도 강제 리다이렉트가 발생하는 버그가 있었다. AuthContext.tsx의
// 인터셉터로 일원화했으므로 여기서는 제거한다.
